/**
 * Day rate × shifts on a per-day labour line, AS THE API REPORTS IT.
 *
 * The rule lives in the API (`resolveDayRateShifts`, api#1181) and is NOT
 * mirrored here. GET /tickets/:id/pay-assignments hands each row the rule's
 * answer: `shifts_rule` says whether the line takes shifts at all, and
 * `shifts_resolution` says how many this hand is paid for, or why it cannot
 * be paid yet. This file only reads those keys. The one sum it does is the
 * office's own typed shifts against the line quantity, which is a hint on an
 * unsaved edit and never a figure that gets paid.
 *
 * ABSENT IS NOT 'none'. A payload from before the read fields existed carries
 * no `shifts_rule`, and that reads as `null` (unknown), never as "this line
 * pays once". The screen then behaves as it always has, and the save door
 * still refuses an uneven split with `needs_shifts`.
 */

export type ShiftsRule = 'none' | 'auto' | 'entry_required';

export type ShiftsSource = 'office' | 'single_hand' | 'even_split';

export type ShiftsResolution =
  | { kind: 'shifts'; shifts: number; source: ShiftsSource }
  | { kind: 'refused'; reason: 'needs_shifts' | 'shifts_mismatch'; message: string };

const RULES: readonly ShiftsRule[] = ['none', 'auto', 'entry_required'];
const SOURCES: readonly ShiftsSource[] = ['office', 'single_hand', 'even_split'];

/** The line's rule, or null when the payload does not say. */
export function shiftsRuleOf(raw: unknown): ShiftsRule | null {
  return typeof raw === 'string' && (RULES as readonly string[]).includes(raw) ? (raw as ShiftsRule) : null;
}

/** Does a line on this rule show a shifts input on its day-rate rows? */
export function takesShifts(rule: ShiftsRule | null): boolean {
  return rule === 'auto' || rule === 'entry_required';
}

/**
 * A positive shifts figure, or null. NUMERIC arrives as a string ("2.00").
 * Zero, negative and garbage are null, not 0: the column CHECKs `> 0`, so
 * a 0 here could only be a misread of an absent value.
 */
export function parseShifts(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** The server's resolution for a row, or null when it sent none or one this screen cannot read. */
export function shiftsResolutionOf(raw: unknown): ShiftsResolution | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.kind === 'shifts') {
    const shifts = parseShifts(r.shifts);
    const source = typeof r.source === 'string' && (SOURCES as readonly string[]).includes(r.source)
      ? (r.source as ShiftsSource) : null;
    return shifts != null && source ? { kind: 'shifts', shifts, source } : null;
  }
  if (r.kind === 'refused' && (r.reason === 'needs_shifts' || r.reason === 'shifts_mismatch')) {
    return { kind: 'refused', reason: r.reason, message: typeof r.message === 'string' ? r.message : '' };
  }
  return null;
}

/**
 * The office's typed shifts on one line, against the line quantity.
 *
 * `complete` is false while any hand has no figure; `adds` is null when the
 * quantity is unknown, because then nothing can be said about the sum.
 */
export function shiftsTally(entered: Array<number | null>, quantity: number | null): {
  sum: number;
  complete: boolean;
  adds: boolean | null;
} {
  const sum = Math.round(entered.reduce<number>((s, v) => s + (v ?? 0), 0) * 100) / 100;
  const complete = entered.length > 0 && entered.every(v => v != null);
  const adds = quantity == null || !Number.isFinite(quantity) ? null : Math.abs(sum - quantity) < 0.005;
  return { sum, complete, adds };
}

/** "1 shift", "2.5 shifts". */
export function fmtShifts(n: number): string {
  const v = Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  return `${v} shift${n === 1 ? '' : 's'}`;
}
